import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { AdminReviewsQueryParams } from '@shopexperts/legacy-api';

export interface UseAdminExpertReviewsOptions {
  status?: AdminReviewsQueryParams['status'];
  source?: AdminReviewsQueryParams['source'];
  limit?: number;
}

export function useAdminExpertReviews(
  expertId: MaybeRefOrGetter<number>,
  opts: UseAdminExpertReviewsOptions = {},
) {
  const api = useApi();
  const toaster = useToaster();

  const limit = opts.limit ?? 20;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'admin',
      'reviews',
      'by-expert',
      computed(() => toValue(expertId)),
      opts.status ?? 'all',
      opts.source ?? 'all',
    ],
    queryFn: ({ pageParam }) => {
      const query: AdminReviewsQueryParams = {
        limit,
        expert_id: toValue(expertId),
      };
      if (opts.status) query.status = opts.status;
      if (opts.source) query.source = opts.source;
      return api.admin.reviewsV2.getAllReviews({
        query,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  watch(error, (newError) => {
    if (newError) {
      toaster.create({
        title: 'Failed to load reviews',
        description: newError.message || 'An error occurred while fetching reviews.',
        type: 'error',
      });
      console.error('[useAdminExpertReviews] Error:', newError);
    }
  });

  const reviews = computed(() => {
    return data.value?.pages.flatMap((page) => page.data) ?? [];
  });

  const stats = computed(() => {
    return data.value?.pages[0]?.meta;
  });

  return {
    reviews,
    stats,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  };
}
